import { DownloadSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SurveyQuestion } from '@/api/communication'
import {
  getSurveyResponders,
  SURVEY_RESPONDERS_QUERY_KEY,
  useGetSurveyResponders,
} from '@/api/communication/surveys/get-survey-responders'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants'
import { toastFactory } from '@/factories'
import { useFilters } from '@/hooks/use-filters'
import { useSearchParams } from '@/hooks/use-search-params'
import {
  SurveyCommunicationType,
  SurveyCommunicationTypeEnum,
  type SurveyResponderStatus,
} from '@/models'
import { getApiErrorMessage } from '@/utils/error'

import {
  getSurveyCommunicationTypeStatusTabs,
  getSurveyTableColumns,
  SURVEY_RECIPIENTS_TABLE_INITIAL_STATE,
  surveyRecipientsTableFiltersSchema,
} from './survey-recipients-table.constants'
import { exportSurveyRecipientsCSV } from './survey-recipients-table.utils'

export interface SurveyRecipientsTableProps {
  surveyId: number
  communicationType: SurveyCommunicationType
  questions: SurveyQuestion[]
  buildingName?: string
  timezone?: string
}

export const SurveyRecipientsTable = ({
  surveyId,
  communicationType,
  questions,
  buildingName,
  timezone = DEFAULT_TZ,
}: SurveyRecipientsTableProps) => {
  const { t } = useTranslation(['communication'])
  const searchParams = useSearchParams({
    initialState: SURVEY_RECIPIENTS_TABLE_INITIAL_STATE,
    schema: surveyRecipientsTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: SURVEY_RECIPIENTS_TABLE_INITIAL_STATE,
    onFiltersChange: searchParams.writeParams,
  })

  const filters = tableFilters.filters
  const search = filters.search?.trim()
  const [isExporting, setIsExporting] = useState(false)

  const handleSearchInputChange = (search: string) => {
    tableFilters.setFilters({ search })
  }

  const { data, isFetching, error } = useGetSurveyResponders({
    limit: filters.limit,
    query: search || undefined,
    skip: filters.skip,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    status: filters.status,
    surveyId,
  })

  // Badges and pagination come from the table's own response: BE `count` is the "All" total
  // (it ignores the status filter) and `statusCounts` holds every status tab's total.
  const counts: Record<string, number> = data ? { '': data.count, ...data.statusCounts } : {}
  const total = counts[filters.status ?? ''] ?? 0

  const statusOptions: StatusTabOption<string>[] = getSurveyCommunicationTypeStatusTabs(t)[
    communicationType
  ].map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }))

  const isInAppCommunication = communicationType === SurveyCommunicationTypeEnum.enum['in-app']

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Survey',
    fieldType: 'resident',
    rowsQueryKey: [SURVEY_RESPONDERS_QUERY_KEY],
  })

  const columns = getSurveyTableColumns(
    t,
    questions,
    isInAppCommunication,
    timezone,
    residentColumnSettings,
  )

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    tableFilters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleExportCSV = async () => {
    if (!total) return

    // TODO: Remove useState for loading - it should be handled by tanstack query
    setIsExporting(true)
    try {
      const allData = await getSurveyResponders({
        limit: total,
        query: search || undefined,
        skip: 0,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        status: filters.status,
        surveyId,
      })

      exportSurveyRecipientsCSV({
        buildingName,
        isInAppCommunication,
        questions,
        responders: allData.responders,
        surveyId,
        t,
        timezone,
      })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <TableGroup
      id="survey-recipients"
      columns={columns}
      data={data?.responders || []}
      emptyNoFilters={{
        description: t('communication:surveyDetails.recipientsTable.empty.noFilters.description'),
        title: t('communication:surveyDetails.recipientsTable.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t('communication:surveyDetails.recipientsTable.empty.withFilters.description'),
        title: t('communication:surveyDetails.recipientsTable.empty.withFilters.title'),
      }}
      error={error}
      enableColumnVisibility={false}
      getRowKey={(responder) => responder.id}
      loading={isFetching}
      navigation={{
        actions: (
          <Button
            onlyBox
            disabled={!total}
            loading={isExporting}
            size="small"
            use="secondary"
            title={t('communication:surveyDetails.recipientsTable.exportCsv')}
            onClick={handleExportCSV}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            name="search"
            placeholder={t('communication:surveyDetails.recipientsTable.search.placeholder')}
            size="small"
            value={filters.search ?? ''}
            onChange={(e) => handleSearchInputChange(e.target.value)}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusOptions}
            value={filters.status ?? ''}
            onChange={(value) =>
              tableFilters.setFilters(
                { status: (value as SurveyResponderStatus) || undefined },
                true,
              )
            }
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total,
      }}
      sortBy={filters.sortBy}
      sortOrder={filters.sortOrder}
      tableFilters={tableFilters}
      onSort={handleSort}
    />
  )
}
